
import { useState, useEffect } from "react";
import { Button, Table, Spin, Alert, Tag, Select, Row, Col, message } from "antd";
import { FileExcelOutlined, ReloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { getDistrict, getBlockByDistrict, getOldAgeSamanAllowanceReport, getVillageByBlock } from "../api/User";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const flattenColumns = (cols) => {
    let result = [];
    cols.forEach((col) => {
        if (col.children) result = result.concat(flattenColumns(col.children));
        else result.push(col);
    });
    return result;
};

const OldAgeAllowance = ({ dateFilter }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const [districtCode, setDistrictCode] = useState(null);
    const [blockCode, setBlockCode] = useState(null);
    const [panchayatCode, setPanchayatCode] = useState(null);
    const [panchayats, setPanchayats] = useState([]);
    const [loadingPanchayat, setLoadingPanchayat] = useState(false);


    const [districts, setDistricts] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [loadingBlocks, setLoadingBlocks] = useState(false);

    const [exportLoading, setExportLoading] = useState(false);
    const [roleId, setRoleId] = useState(null);
    // 🔹 Grand totals (Current Page - Unique GPs)
    const grandTotals = (() => {
        const uniqueGPs = {};
        data.forEach(item => {
            const gpKey = `${item.district_code}_${item.block_code}_${item.panchayat_code}`;
            if (!uniqueGPs[gpKey]) {
                uniqueGPs[gpKey] = {
                    total_members: Number(item.total_members) || 0,
                    objection_members: Number(item.objection_members) || 0
                };
            }
        });
        return {
            total_members: Object.values(uniqueGPs).reduce((s, i) => s + i.total_members, 0),
            objection_members: Object.values(uniqueGPs).reduce((s, i) => s + i.objection_members, 0),
        };
    })();

    useEffect(() => {
        const role = sessionStorage.getItem("role_id");
        setRoleId(role);
        fetchDistricts();
    }, []);

    useEffect(() => {
        if (districtCode) {
            fetchBlocks(districtCode);
        } else {
            setBlocks([]);
        }
    }, [districtCode]);
    useEffect(() => {
        if (districtCode && blockCode) {
            fetchPanchayats(districtCode, blockCode);
        } else {
            setPanchayats([]);
        }
    }, [districtCode, blockCode]);


    useEffect(() => {
        setCurrentPage(1);
        fetchReport(1, pageSize);
    }, [districtCode, blockCode, panchayatCode, dateFilter]);

    const fetchDistricts = async () => {
        try {
            setLoadingDistricts(true);
            const token = sessionStorage.getItem("token");
            const userId = sessionStorage.getItem("user_id");
            const role = sessionStorage.getItem("role_id");

            const res = await getDistrict(token, userId, role);
            if (res.status === "success" && res.data) {
                setDistricts(res.data);

                // Auto-select district for BDPO (role 7) and Sarpanch (role 3)
                if ((role === "7" || role === "3") && res.data.length === 1) {
                    setDistrictCode(res.data[0].district_code);
                }
            }
        } catch (err) {
            console.error("Error fetching districts:", err);
            message.error("Failed to load districts");
        } finally {
            setLoadingDistricts(false);
        }
    };

    const fetchBlocks = async (selectedDistrictCode) => {
        try {
            setLoadingBlocks(true);
            const token = sessionStorage.getItem("token");
            const userId = sessionStorage.getItem("user_id");
            const role = sessionStorage.getItem("role_id");

            const res = await getBlockByDistrict(token, selectedDistrictCode, userId, role);
            if (res.status === "success" && res.data) {
                setBlocks(res.data);

                // Auto-select block for BDPO (role 7)
                if (role === "7" && res.data.length === 1) {
                    setBlockCode(res.data[0].block_code);
                }
            }
        } catch (err) {
            console.error("Error fetching blocks:", err);
            message.error("Failed to load blocks");
        } finally {
            setLoadingBlocks(false);
        }
    };

    const fetchPanchayats = async (district, block) => {
        try {
            setLoadingPanchayat(true);
            const token = sessionStorage.getItem("token");
            const userId = sessionStorage.getItem("user_id");
            const role = sessionStorage.getItem("role_id");

            const res = await getVillageByBlock(token, district, block, userId, role);

            if (res.status === "success") {
                setPanchayats(res.data);
            } else {
                setPanchayats([]);
            }
        } catch (err) {
            message.error("Failed to load Gram Panchayats");
        } finally {
            setLoadingPanchayat(false);
        }
    };


    const columns = [
        {
            title: "SR.NO.",
            dataIndex: "sr_no",
            width: 60,
            fixed: "left",
            align: "center",
            render: (t, r, i) => (currentPage - 1) * pageSize + i + 1
        },
        {
            title: "District",
            dataIndex: "district_name",
            align: "center",
            sorter: (a, b) => {
                const distA = (a.district_name || '').toLowerCase();
                const distB = (b.district_name || '').toLowerCase();
                return distA.localeCompare(distB);
            },
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
        },
        {
            title: "Block",
            dataIndex: "block_name",
            align: "center",
            sorter: (a, b) => {
                const blockA = (a.block_name || '').toLowerCase();
                const blockB = (b.block_name || '').toLowerCase();
                return blockA.localeCompare(blockB);
            },
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
        },
        {
            title: "Gram Panchayat",
            dataIndex: "panchayat_name",
            align: "center",
            sorter: (a, b) => {
                const gpA = (a.panchayat_name || '').toLowerCase();
                const gpB = (b.panchayat_name || '').toLowerCase();
                return gpA.localeCompare(gpB);
            },
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
        },
        {
            title: "Quorum Status",
            dataIndex: "sabha_status",
            align: "center",
            sorter: (a, b) => {
                const statusA = (a.sabha_status || '').toLowerCase();
                const statusB = (b.sabha_status || '').toLowerCase();
                return statusA.localeCompare(statusB);
            },
            sortDirections: ['ascend', 'descend'],
            render: (val) => {
                const status = val || "Failed";
                return (
                    <Tag
                        color={status === "Fulfilled" ? "green" : "red"}
                        style={{ fontSize: "10px" }}
                    >
                        {status}
                    </Tag>
                );
            }
        },
        {
            title: "Member Name",
            dataIndex: "member_name",
            align: "center",
            sorter: (a, b) => {
                const nameA = (a.member_name || '').toLowerCase();
                const nameB = (b.member_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            },
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: "Old Age Samman Allowance Members",
            dataIndex: "total_members",
            align: "center",
            sorter: (a, b) => (Number(a.total_members) || 0) - (Number(b.total_members) || 0),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: "No of Objections",
            dataIndex: "objection_members",
            align: "center",
            sorter: (a, b) => (Number(a.objection_members) || 0) - (Number(b.objection_members) || 0),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: "Family ID",
            dataIndex: "family_ppp_id",
            align: "center",
            sorter: (a, b) => {
                const idA = (a.family_ppp_id || '').toLowerCase();
                const idB = (b.family_ppp_id || '').toLowerCase();
                return idA.localeCompare(idB);
            },
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: "Member ID",
            dataIndex: "member_ppp_id",
            align: "center",
            sorter: (a, b) => {
                const idA = (a.member_ppp_id || '').toLowerCase();
                const idB = (b.member_ppp_id || '').toLowerCase();
                return idA.localeCompare(idB);
            },
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: "Remarks",
            dataIndex: "gs_scheme_remark",
            align: "center",
            sorter: (a, b) => {
                const remarkA = (a.gs_scheme_remark || '').toLowerCase();
                const remarkB = (b.gs_scheme_remark || '').toLowerCase();
                return remarkA.localeCompare(remarkB);
            },
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
        },
    ];

    const fetchReport = async (page = 1, size = pageSize) => {
        try {
            setLoading(true);
            setError(null);
            const token = sessionStorage.getItem("token");

            const payload = {
                district_code: districtCode || undefined,
                block_code: blockCode || undefined,
                panchayat_code: panchayatCode || undefined,
                page,
                limit: size,
                period: dateFilter
            };

            Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

            const res = await getOldAgeSamanAllowanceReport(token, payload);

            // Assuming API response structure is similar to SpecialGramSabhaStatus
            if (!res.success) {
                setError(res.message || "Unable to load data");
                return;
            }

            const formatted = res.result?.map((row, index) => ({
                key: index + 1,
                sr_no: (page - 1) * size + index + 1,
                ...row,
            })) || [];

            setData(formatted);
            setTotalRecords(res.pagination?.total || formatted.length);
            setCurrentPage(page);
            setPageSize(size);

        } catch (err) {
            console.error("Fetch Report Error:", err);
            setError("Failed to load report data");
        } finally {
            setLoading(false);
        }
    };

    const fetchAllDataForExport = async () => {
        try {
            setExportLoading(true);
            const token = sessionStorage.getItem("token");

            // First, get the total count with a small limit
            const initialPayload = {
                page: 1,
                limit: 10,
                district_code: districtCode || undefined,
                block_code: blockCode || undefined,
                panchayat_code: panchayatCode || undefined,
                period: dateFilter
            };

            Object.keys(initialPayload).forEach(k => initialPayload[k] === undefined && delete initialPayload[k]);

            // Get initial data to know total records
            const initialResult = await getOldAgeSamanAllowanceReport(token, initialPayload);

            if (!initialResult.success || !initialResult.result || initialResult.result.length === 0) {
                message.warning("No data available to export");
                return [];
            }

            const totalRecords = initialResult.pagination?.total || 0;

            // If total records are less than or equal to 1000, fetch all at once
            if (totalRecords <= 1000) {
                const allDataPayload = {
                    ...initialPayload,
                    page: 1,
                    limit: totalRecords,
                };

                const allDataResult = await getOldAgeSamanAllowanceReport(token, allDataPayload);

                if (allDataResult.success && allDataResult.result) {
                    console.log(`Fetched all ${allDataResult.result.length} records at once`);
                    return allDataResult.result;
                }
            }

            // If more than 1000 records, fetch in batches
            const allData = [];
            const batchSize = 1000;
            const totalBatches = Math.ceil(totalRecords / batchSize);

            message.loading({
                content: `Fetching data (0/${totalRecords})...`,
                key: 'fetchProgress',
                duration: 0,
            });

            for (let page = 1; page <= totalBatches; page++) {
                const batchPayload = {
                    ...initialPayload,
                    page: page,
                    limit: batchSize,
                };

                const batchResult = await getOldAgeSamanAllowanceReport(token, batchPayload);

                if (batchResult.success && batchResult.result) {
                    allData.push(...batchResult.result);

                    message.loading({
                        content: `Fetching data (${allData.length}/${totalRecords})...`,
                        key: 'fetchProgress',
                        duration: 0,
                    });
                }

                if (page < totalBatches) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            message.destroy('fetchProgress');
            console.log(`Total records fetched: ${allData.length}`);
            return allData;
        } catch (err) {
            console.error("Fetch All Data Error:", err);
            message.destroy('fetchProgress');
            message.error("Failed to fetch data for export");
            return [];
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportPDF = async () => {
        try {
            if (exportLoading || !totalRecords) {
                message.warning("No data available to export");
                return;
            }

            message.loading({
                content: "Preparing PDF export...",
                key: "exportPdf",
                duration: 0,
            });

            const allData = await fetchAllDataForExport();

            if (!allData.length) {
                message.destroy("exportPdf");
                message.warning("No data to export");
                return;
            }

            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
            });

            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFontSize(14);
            doc.text("Old Age Samman Allowance Scheme - Objection Status", pageWidth / 2, 12, {
                align: "center",
            });

            doc.setFontSize(9);
            let filterText = "Generated on: " + new Date().toLocaleString();

            if (districtCode || blockCode || panchayatCode) {
                filterText += " | Filters: ";
                if (districtCode) {
                    const district = districts.find(d => d.district_code === districtCode);
                    filterText += `District: ${district?.district || districtCode} `;
                }
                if (blockCode) {
                    const block = blocks.find(b => b.block_code === blockCode);
                    filterText += `Block: ${block?.block || blockCode} `;
                }

                if (panchayatCode) {
                    const panchayat = panchayats.find(p => p.gram_panchayat_code === panchayatCode);
                    filterText += `Gram Panchayat: ${panchayat?.gram_panchayat || panchayatCode} `;
                }
            }

            doc.text(filterText, pageWidth / 2, 18, { align: "center" });

            const flatColumns = flattenColumns(columns);
            const headers = flatColumns.map(col => col.title);

            const body = allData.map((row, index) =>
                flatColumns.map(col => {
                    if (col.dataIndex === "sr_no") {
                        return index + 1;
                    }
                    const value = row[col.dataIndex];
                    if (value === null || value === undefined || value === "") {
                        return "";
                    }
                    return value;
                })
            );
            // 🔹 Calculate totals for ALL pages
            const uniqueGPs = {};
            allData.forEach(item => {
                const gpKey = `${item.district_code}_${item.block_code}_${item.panchayat_code}`;
                if (!uniqueGPs[gpKey]) {
                    uniqueGPs[gpKey] = {
                        total_members: Number(item.total_members) || 0,
                        objection_members: Number(item.objection_members) || 0
                    };
                }
            });

            const pdfTotals = {
                total_members: Object.values(uniqueGPs).reduce((s, i) => s + i.total_members, 0),
                objection_members: Object.values(uniqueGPs).reduce((s, i) => s + i.objection_members, 0),
            };

            // 🔹 Push total row
            body.push(
                flatColumns.map(col => {
                    if (col.dataIndex === "district_name") return "GRAND TOTAL";
                    if (col.dataIndex === "total_members") return pdfTotals.total_members;
                    if (col.dataIndex === "objection_members") return pdfTotals.objection_members;
                    if (col.dataIndex === "sr_no") return "";
                    return "";
                })
            );


            autoTable(doc, {
                startY: 24,
                head: [headers],
                body,
                theme: "grid",
                styles: {
                    fontSize: 6,
                    cellPadding: 1.5,
                    halign: "center",
                    valign: "middle",
                    overflow: "linebreak",
                },
                headStyles: {
                    fillColor: [22, 119, 255],
                    textColor: 255,
                    fontStyle: "bold",
                },
                margin: { left: 5, right: 5 },
                tableWidth: "auto",
                didParseCell: function (data) {
                    if (data.row.index === body.length - 1) {
                        data.cell.styles.fillColor = [240, 249, 255];
                        data.cell.styles.fontStyle = "bold";
                    }
                }

            });

            doc.save(`Old_Age_Saman_Allowance_Report_${Date.now()}.pdf`);

            message.destroy("exportPdf");
            message.success(`PDF exported successfully with ${allData.length} records`);
        } catch (err) {
            console.error("PDF Export Error:", err);
            message.destroy("exportPdf");
            message.error("Failed to export PDF");
        }
    };

    const handleExportExcel = async () => {
        try {
            if (exportLoading || !totalRecords) {
                message.warning("No data available to export");
                return;
            }

            message.loading({
                content: "Preparing Excel export...",
                key: "exportExcel",
                duration: 0,
            });

            const allData = await fetchAllDataForExport();

            if (!allData.length) {
                message.destroy("exportExcel");
                message.warning("No data to export");
                return;
            }

            const flatColumns = flattenColumns(columns);
            const headers = flatColumns.map(col => `"${col.title}"`);

            // 🔹 Calculate totals for ALL pages
            const uniqueGPs = {};
            allData.forEach(item => {
                const gpKey = `${item.district_code}_${item.block_code}_${item.panchayat_code}`;
                if (!uniqueGPs[gpKey]) {
                    uniqueGPs[gpKey] = {
                        total_members: Number(item.total_members) || 0,
                        objection_members: Number(item.objection_members) || 0
                    };
                }
            });

            const excelTotals = {
                total_members: Object.values(uniqueGPs).reduce((s, i) => s + i.total_members, 0),
                objection_members: Object.values(uniqueGPs).reduce((s, i) => s + i.objection_members, 0),
            };

            const dataRows = allData.map((row, index) =>
                flatColumns
                    .map(col => {
                        if (col.dataIndex === "sr_no") {
                            return `"${index + 1}"`;
                        }
                        const value = row[col.dataIndex];
                        return `"${value ?? ""}"`;
                    })
                    .join(",")
            );

            // 🔹 Add total row BEFORE CSV creation
            dataRows.push(
                flatColumns.map(col => {
                    if (col.dataIndex === "district_name") return `"GRAND TOTAL"`;
                    if (col.dataIndex === "total_members") return `"${excelTotals.total_members}"`;
                    if (col.dataIndex === "objection_members") return `"${excelTotals.objection_members}"`;
                    if (col.dataIndex === "sr_no") return `""`;
                    return `""`;
                }).join(",")
            );

            const csvContent = [
                headers.join(","),
                ...dataRows
            ].join("\n");

            const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute(
                "download",
                `Old_Age_Saman_Allowance_Report_${new Date().toISOString().split("T")[0]}.csv`
            );

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.destroy("exportExcel");
            message.success(`Report exported successfully with ${allData.length} records!`);

        } catch (err) {
            console.error("Excel Export Error:", err);
            message.destroy("exportExcel");
            message.error("Failed to export Excel");
        }
    };


    const handleTableChange = (pagination, filters, sorter) => {
        const { current, pageSize: newPageSize } = pagination;

        if (current !== currentPage || newPageSize !== pageSize) {
            setCurrentPage(current);
            setPageSize(newPageSize);
            fetchReport(current, newPageSize);
        }
    };

    const handleClearFilters = () => {
        setDistrictCode(null);
        setBlockCode(null);
        setPanchayatCode(null);

        setBlocks([]);       // clear block dropdown
        setPanchayats([]);   // clear panchayat dropdown

        setCurrentPage(1);   // reset table page
    };


    return (
        <div className="report-container">
            <div className="report-card">

                {/* HEADER */}
                <div className="report-header">
                    <div>
                        <h2 className="report-title">
                            Special Gram Sabha Objection Status – Old Age Saman Allowance Scheme

                        </h2>
                        <p className="report-subtitle">
                            Total Records: <strong>{totalRecords}</strong>
                        </p>
                    </div>

                    <div className="report-actions">
                        <Button
                            onClick={handleExportPDF}
                            icon={<FilePdfOutlined />}
                            disabled={!data.length || exportLoading}
                            loading={exportLoading}
                        >
                            Export PDF
                        </Button>

                        <Button
                            type="primary"
                            onClick={handleExportExcel}
                            icon={<FileExcelOutlined />}

                            disabled={!data.length || exportLoading}
                            loading={exportLoading}
                        >
                            Export Excel
                        </Button>

                        <Button
                            onClick={() => fetchReport(currentPage, pageSize)}
                            icon={<ReloadOutlined />}
                            className="btn-refresh"
                            disabled={exportLoading}
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* FILTERS */}
                <Row gutter={12} style={{ marginBottom: 16 }}>
                    {/* Show District filter for CEO (8), Admin (1), and HPPA (5) */}
                    {(roleId === "8" || roleId === "1" || roleId === "5" || roleId === "6") && (
                        <Col xs={24} sm={6}>
                            <Select
                                allowClear
                                placeholder="Select District"
                                style={{ width: "100%" }}
                                value={districtCode}
                                loading={loadingDistricts}
                                onChange={(val) => {
                                    setDistrictCode(val);
                                    setBlockCode(null);
                                }}
                            >
                                {districts.map((d) => (
                                    <Select.Option key={d.district_code} value={d.district_code}>
                                        {d.district}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                    )}

                    {/* Show Block filter for CEO (8), BDPO (7), Admin (1), and HPPA (5) */}
                    {(roleId === "8" || roleId === "7" || roleId === "1" || roleId === "5" || roleId === "6") && (
                        <Col xs={24} sm={6}>
                            <Select
                                allowClear
                                placeholder="Select Block"
                                style={{ width: "100%" }}
                                value={blockCode}
                                loading={loadingBlocks}
                                disabled={!districtCode && (roleId === "8" || roleId === "1" || roleId === "5" || roleId === "6")}
                                onChange={(val) => setBlockCode(val)}
                            >
                                {blocks.map((b) => (
                                    <Select.Option key={b.block_code} value={b.block_code}>
                                        {b.block}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                    )}

                    {/* Date filter available for all roles */}
                    <Col xs={24} sm={6}>
                        <Select
                            allowClear
                            placeholder="Select Gram Panchayat"
                            style={{ width: "100%" }}
                            value={panchayatCode}
                            loading={loadingPanchayat}
                            disabled={!blockCode}
                            onChange={(val) => setPanchayatCode(val)}
                        >
                            {panchayats.map((gp) => (
                                <Select.Option
                                    key={gp.gram_panchayat_code}
                                    value={gp.gram_panchayat_code}
                                >
                                    {gp.gram_panchayat}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>


                    <Col xs={24} sm={6}>
                        <Button
                            danger
                            block
                            onClick={handleClearFilters}
                            disabled={!districtCode && !blockCode && !panchayatCode}
                        >
                            Clear All Filters
                        </Button>
                    </Col>
                </Row>

                {error && (
                    <Alert
                        type="error"
                        message="Error loading data"
                        description={error}
                        showIcon
                        closable
                        onClose={() => setError(null)}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {loading ? (
                    <div className="loading-container">
                        <Spin size="large" tip="Loading report..." />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            total: totalRecords,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} records`,
                        }}
                        onChange={handleTableChange}
                        bordered
                        scroll={{ x: 1500, y: 420 }}
                        size="small"
                    />
                )}
                {data.length > 0 && (
                    <div
                        style={{
                            marginTop: 12,
                            padding: "10px 14px",
                            background: "#e6f7ff",
                            border: "1px solid #91d5ff",
                            borderRadius: 6,
                            display: "flex",
                            gap: 20,
                            fontSize: 13,
                        }}
                    >
                        <strong>Grand Totals (Current Page):</strong>

                        <span>
                            Total panchayat members received by objection :
                            <b> {grandTotals.total_members}</b>
                        </span>

                        <span>
                            Total Objections:
                            <b> {grandTotals.objection_members}</b>
                        </span>
                    </div>
                )}


            </div>
        </div>
    );
};

export default OldAgeAllowance;


